import styled from "styled-components"

import {
  ChevronLeftIcon,
  HomeIcon,
  SensorIcon,
  SlidersIcon,
  TeamsIcon,
} from "@minuthq/meatball-ui-icons"
import {
  ButtonStyleReset,
  colors,
  fontWeight,
} from "@minuthq/meatball-ui-react"

import { ParadiseProfileButton } from "src/components/AppLayout/ParadiseProfileButton"
import { NavLinkGroup } from "src/components/AppLayout/SideNav/NavLinkGroup"
import { ParadiseEcommerceNavPopover } from "src/components/Paradise/ParadiseEcommerce/ParadiseEcommerceNavPopover"
import { RecentlyViewedPopover } from "src/components/RecentlyViewed/RecentlyViewedPopover"
import { RequireRoles } from "src/components/RestrictedRoles/RequireRoles"
import { useStorage } from "src/data/storage/useStorage"
import { UserRoles } from "src/data/user/user"
import { Routes } from "src/router/routes"
import { Divider } from "src/ui/Divider/Divider"
import CartIcon from "src/ui/icons/cart.svg"
import CodeIcon from "src/ui/icons/code.svg"
import Logo from "src/ui/icons/logo.svg"
import OrganizationIcon from "src/ui/icons/organization.svg"
import StopwatchIcon from "src/ui/icons/stopwatch.svg"
import ToolIcon from "src/ui/icons/tool.svg"
import { spacing } from "src/ui/spacing"
import { localStorageFactory } from "src/utils/storageUtil"

import {
  IconBox,
  LogoBox,
  NavFooter,
  NavMain,
  SharedStyledNavLink,
  SideNavBox,
} from "./sharedStyles"

const collapsedStorage = localStorageFactory({
  key: "paradise-side-nav-collapsed",
})

export function ParadiseSideNav({
  navLinkClick = () => {},
  ...props
}: {
  navLinkClick?: () => void
}) {
  const { data: isCollapsedStorage, set: setIsCollapsed } = useStorage({
    storage: collapsedStorage,
    initialValue: "false",
  })

  const isCollapsed = isCollapsedStorage === "true"

  return (
    <StyledSideNavBox
      {...props}
      aria-label="navbar"
      $background={colors.textPrimary}
      $isCollapsed={isCollapsed}
    >
      <StyledLogoBox $isCollapsed={isCollapsed}>
        {!isCollapsed && <Logo width={70} fill="#fff" />}
        <ParadiseProfileButton showLabel={false} />
      </StyledLogoBox>

      <NavMain
        $color={colors.textContrast}
        $hoverBgColor="rgba(255, 255, 255, 0.15)"
        $activeBgColor="rgba(255, 255, 255, 0.15)"
        $activeColor={colors.textContrast}
      >
        <li>
          <StyledNavLink
            to={Routes.ParadiseUsers.location()}
            onClick={navLinkClick}
            $isCollapsed={isCollapsed}
          >
            <IconBox $isCollapsed={isCollapsed}>
              <TeamsIcon />
            </IconBox>
            {!isCollapsed && "Users"}
          </StyledNavLink>
        </li>
        <li>
          <StyledNavLink
            to={Routes.ParadiseHomes.location()}
            onClick={navLinkClick}
            $isCollapsed={isCollapsed}
          >
            <IconBox $isCollapsed={isCollapsed}>
              <HomeIcon />
            </IconBox>
            {!isCollapsed && "Homes"}
          </StyledNavLink>
        </li>
        <li>
          <StyledNavLink
            to={Routes.ParadiseDevices.location()}
            onClick={navLinkClick}
            $isCollapsed={isCollapsed}
          >
            <IconBox $isCollapsed={isCollapsed}>
              <SensorIcon />
            </IconBox>
            {!isCollapsed && "Devices"}
          </StyledNavLink>
        </li>

        <li>
          <StyledNavLink
            to={Routes.ParadiseOrganizations.location().pathname}
            onClick={navLinkClick}
            $isCollapsed={isCollapsed}
          >
            <IconBox $isCollapsed={isCollapsed}>
              <OrganizationIcon />
            </IconBox>
            {!isCollapsed && "Organizations"}
          </StyledNavLink>
        </li>
        <RequireRoles roles={[UserRoles.SUPERADMIN]}>
          <li>
            <StyledNavLink
              to={Routes.ParadiseProfiles.location()}
              onClick={navLinkClick}
              $isCollapsed={isCollapsed}
            >
              <IconBox $isCollapsed={isCollapsed}>
                <SlidersIcon color="white" />
              </IconBox>
              {!isCollapsed && "Profiles"}
            </StyledNavLink>
          </li>
          <li>
            <StyledNavLink
              to={Routes.ParadiseClients.location()}
              onClick={navLinkClick}
              $isCollapsed={isCollapsed}
            >
              <IconBox $isCollapsed={isCollapsed}>
                <CodeIcon color="white" />
              </IconBox>
              {!isCollapsed && "Clients"}
            </StyledNavLink>
          </li>
          <li>
            <StyledNavLink
              to={Routes.ParadiseFirmwares.location()}
              onClick={navLinkClick}
              $isCollapsed={isCollapsed}
            >
              <IconBox $isCollapsed={isCollapsed}>
                <ToolIcon />
              </IconBox>
              {!isCollapsed && "Firmwares"}
            </StyledNavLink>
          </li>
        </RequireRoles>

        <li>
          {isCollapsed ? (
            <ParadiseEcommerceNavPopover
              trigger={
                <StyledNavLink
                  to=""
                  isActive={() => false}
                  $isCollapsed={isCollapsed}
                >
                  <IconBox $isCollapsed={isCollapsed}>
                    <CartIcon />
                  </IconBox>
                </StyledNavLink>
              }
              navlinks={[
                {
                  to: Routes.ParadiseOrders.location(),
                  children: "Orders",
                },
                {
                  to: Routes.ParadiseShippableProducts.location(),
                  children: "Shippable products",
                },
                {
                  to: Routes.ParadiseShipping.location(),
                  children: "Shipping",
                },
                {
                  to: Routes.ParadiseQuotes.location(),
                  children: "Quotes",
                },
              ]}
            />
          ) : (
            <StyledNavLinkGroup
              id="e_commerce"
              title="E-commerce"
              disabledWhenSuspended={false}
              icon={<CartIcon />}
              navlinks={[
                {
                  to: Routes.ParadiseOrders.location(),
                  children: "Orders",
                },
                {
                  to: Routes.ParadiseShippableProducts.location(),
                  children: "Shippable products",
                },
                {
                  to: Routes.ParadiseShipping.location(),
                  children: "Shipping",
                },
                {
                  to: Routes.ParadiseWarehouses.location(),
                  children: "Warehouses",
                },
                {
                  to: Routes.ParadiseQuotes.location(),
                  children: "Quotes",
                },
              ]}
            />
          )}
        </li>

        <RequireRoles roles={[UserRoles.SUPERADMIN]}>
          <StyledDivider />

          <li>
            <StyledNavLink
              to={Routes.ParadiseLLMPrompts.location()}
              onClick={navLinkClick}
              $isCollapsed={isCollapsed}
            >
              <IconBox $isCollapsed={isCollapsed}>
                <CodeIcon color="white" />
              </IconBox>
              {!isCollapsed && "LLM Prompts"}
            </StyledNavLink>
          </li>
        </RequireRoles>

        <StyledDivider />

        <li>
          <RecentlyViewedPopover
            trigger={
              <StyledNavLink
                to=""
                isActive={() => false}
                $isCollapsed={isCollapsed}
              >
                <IconBox $isCollapsed={isCollapsed}>
                  <StopwatchIcon fill={colors.neutralLight} />
                </IconBox>
                {!isCollapsed && "Recently viewed"}
              </StyledNavLink>
            }
          />
        </li>
      </NavMain>

      <StyledNavFooter>
        <CollapseButton
          onClick={() => setIsCollapsed(isCollapsed ? "false" : "true")}
        >
          <ChevronLeftIcon width={24} height={24} />
          {!isCollapsed && "Collapse"}
        </CollapseButton>
      </StyledNavFooter>
    </StyledSideNavBox>
  )
}

const StyledNavLink = styled(SharedStyledNavLink)<{ $isCollapsed: boolean }>`
  color: ${colors.neutralLight};
  justify-content: ${({ $isCollapsed }) =>
    $isCollapsed ? "center" : "flex-start"};
`

const StyledNavLinkGroup = styled(NavLinkGroup)`
  color: ${colors.neutralLight};
`

export const StyledDivider = styled(Divider)`
  margin: ${spacing.M} 0;
  background: ${colors.divider}20;
`

const StyledSideNavBox = styled(SideNavBox)<{ $isCollapsed: boolean }>`
  width: ${({ $isCollapsed }) => ($isCollapsed ? "70px" : "250px")};
`

const StyledLogoBox = styled(LogoBox)`
  display: flex;
  justify-content: space-between;
`

const StyledNavFooter = styled(NavFooter)`
  padding-bottom: ${spacing.M};
`

export const CollapseButton = styled.button`
  ${ButtonStyleReset}

  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: ${spacing.S};
  color: ${colors.neutralLight};
  font-weight: ${fontWeight.light};
  padding: ${spacing.XS};
  border-radius: 5px;
  margin-inline: ${spacing.S};

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`
